/**
 * UN SOLO canal de tiempo real de avisos por usuario, compartido por todos los
 * sitios que los pintan (auditoría del portal del perito, 26-09-2026: A21/T04-03).
 *
 * EL FALLO. `useNotifications` está montado cinco o seis veces a la vez (layout,
 * cabecera, barra superior, hojas de perfil, campana) y cada copia abría un canal
 * con el MISMO nombre fijo, `notifications-realtime`. realtime-js devuelve el canal
 * que ya existe con ese nombre, así que las copias se añadían como bindings a un
 * canal ya unido (el servidor responde «mismatch» y lo cierra) y, al desmontarse
 * una sola, `removeChannel` lo quitaba para todas. En el navegador se veía
 * `phx_join → phx_reply ok → phx_leave → phx_close` y el perito no recibía nada en
 * directo: ni aviso emergente ni campana hasta recargar.
 *
 * AHORA. La primera copia que se suscribe abre el canal (con nombre PROPIO del
 * usuario y de la empresa) y las demás se apuntan a la lista de oyentes; el canal
 * se cierra cuando se va la última. El filtro es `user_id=eq.<yo>`: el servidor
 * solo manda los avisos de quien mira (antes filtraba por empresa y el navegador
 * recibía, y descartaba, los de todos: con la RLS de la tanda 1 eso ya no pasa,
 * pero con este filtro tampoco hace falta que pase). Los avisos «generales» (sin
 * usuario) no llegan por aquí; no se emite ninguno desde febrero de 2026 y, por si
 * vuelven, la consulta se refresca sola cada minuto y al volver a la ventana.
 */
import type { SupabaseClient } from "@supabase/supabase-js";

export interface AvisoEnDirecto {
  id: string;
  company_id: string | null;
  user_id: string | null;
  [k: string]: unknown;
}

type Oyente = (aviso: AvisoEnDirecto) => void;

interface CanalCompartido {
  canal: ReturnType<SupabaseClient["channel"]>;
  oyentes: Set<Oyente>;
}

const canales = new Map<string, CanalCompartido>();

/** Nombre del canal: propio del usuario y de la empresa, nunca uno fijo. */
export function nombreDelCanal(userId: string, companyId: string): string {
  return `notifications:${userId}:${companyId}`;
}

/**
 * Se apunta a los avisos en directo de `userId` en `companyId`. Devuelve la
 * función para desapuntarse. Cuantas más copias se apunten, sigue habiendo UN
 * canal.
 */
export function escucharAvisos(
  client: Pick<SupabaseClient, "channel" | "removeChannel">,
  userId: string,
  companyId: string,
  oyente: Oyente,
): () => void {
  const clave = nombreDelCanal(userId, companyId);
  let entrada = canales.get(clave);
  if (!entrada) {
    const oyentes = new Set<Oyente>();
    const canal = client
      .channel(clave)
      .on(
        "postgres_changes" as never,
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` },
        (payload: { new: AvisoEnDirecto }) => {
          const aviso = payload.new;
          if (!aviso || aviso.user_id !== userId) return;
          // Un aviso de otra empresa (perito con varias) no se pinta en esta.
          if (aviso.company_id && aviso.company_id !== companyId) return;
          for (const o of [...oyentes]) {
            try {
              o(aviso);
            } catch {
              /* un oyente roto no deja sin aviso a los demás */
            }
          }
        },
      )
      .subscribe();
    entrada = { canal, oyentes };
    canales.set(clave, entrada);
  }
  entrada.oyentes.add(oyente);

  return () => {
    const actual = canales.get(clave);
    if (!actual) return;
    actual.oyentes.delete(oyente);
    if (actual.oyentes.size === 0) {
      canales.delete(clave);
      void client.removeChannel(actual.canal);
    }
  };
}

/** Solo para las pruebas. */
export function __canalesAbiertos(): string[] {
  return [...canales.keys()];
}
